// Franchisee-facing contracts list + acceptance flow.
//
// [22] refactor: the review-and-sign panel is now the shared
// ``<ContractReviewAndSign>`` component so the Hub and Launch Portal
// surfaces stay in sync forever.
import { useCallback, useEffect, useMemo, useState } from "react";
import api, { API_BASE as API, getAccessToken } from "@/lib/api";
import ContractReviewAndSign from "@/components/contracts/ContractReviewAndSign";
import {
  Loader2, AlertTriangle, ExternalLink, FileSignature,
} from "lucide-react";

function StatusPill({ status }) {
  const styles = {
    issued:     "bg-emerald-50 text-emerald-800 border-emerald-300",
    signed:     "bg-sky-50 text-sky-800 border-sky-300",
    superseded: "bg-stone-50 text-stone-500 border-stone-200",
  };
  const labels = { issued: "Awaiting your acceptance", signed: "Signed", superseded: "Superseded" };
  return (
    <span data-testid={`portal-contract-status-${status}`}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border font-medium ${styles[status] || ""}`}>
      {labels[status] || status}
    </span>
  );
}

function ContractsBody() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/portal/contracts");
      setRows(data.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (err) {
    return (
      <div className="p-3 bg-red-50 border border-red-200 text-red-800 text-sm rounded flex items-center gap-2"
           data-testid="portal-contracts-error">
        <AlertTriangle className="h-4 w-4" /> {err}
      </div>
    );
  }
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-stone-500 py-10 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="p-6 text-center text-stone-500 border border-dashed rounded-md text-sm"
           data-testid="portal-contracts-empty">
        You don&apos;t have any contracts yet.
      </div>
    );
  }
  return (
    <>
      <div className="space-y-3">
        {rows.map((c) => (
          <div key={c.id} className="border rounded-lg bg-white p-4 flex items-center justify-between gap-4"
               data-testid={`portal-contract-row-${c.id}`}>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <StatusPill status={c.status} />
                <span className="text-xs text-stone-500">{c.contract_reference || c.id.slice(0, 12)}</span>
              </div>
              <div className="text-sm text-stone-700 mt-1">
                Issued: {c.issued_at ? new Date(c.issued_at).toLocaleString("en-GB") : "—"}
                {c.signed_at && (
                  <span className="ml-3">Signed: {new Date(c.signed_at).toLocaleString("en-GB")}</span>
                )}
              </div>
            </div>
            <button
              onClick={() => setActive(c)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-md bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700"
              data-testid={`portal-contract-open-${c.id}`}>
              <ExternalLink className="h-3.5 w-3.5" /> Open
            </button>
          </div>
        ))}
      </div>
      {active && (
        <ContractDetail
          contract={active}
          onClose={() => setActive(null)}
          onSigned={async (updatedContract) => {
            setActive(updatedContract);
            await load();
          }}
        />
      )}
    </>
  );
}

export default function PortalContractsPage() {
  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="portal-contracts-page">
      <h1 className="text-2xl font-semibold mb-4" data-testid="portal-contracts-heading">My Contracts</h1>
      <ContractsBody />
    </div>
  );
}

export function PortalContractsSection() {
  return (
    <section
      className="bg-white border border-stone-200 rounded-2xl px-4 sm:px-6 py-5 sm:py-6"
      data-testid="portal-my-contracts-section"
    >
      <div className="flex items-center gap-3 mb-5 pb-4 border-b border-stone-200">
        <FileSignature className="w-6 h-6 text-stone-700 shrink-0" />
        <h1 className="font-display text-2xl sm:text-3xl font-black text-stone-950 tracking-tight">
          My Contracts
        </h1>
      </div>
      <ContractsBody />
    </section>
  );
}

function ContractDetail({ contract, onClose, onSigned }) {
  const [row, setRow] = useState(contract);
  const [downloadUrl, setDownloadUrl] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const legacyEndpoint = row.status === "signed"
          ? `/portal/contracts/${row.id}/signed-pdf`
          : `/portal/contracts/${row.id}/personalised-pdf`;
        const { data } = await api.get(legacyEndpoint);
        setDownloadUrl(data.url);
      } catch {
        /* download link is a nice-to-have; ignore failures */
      }
    })();
  }, [row.id, row.status]);

  const variant = row.status === "signed" ? "signed" : "personalised";
  const pdfUrl = `${API}/portal/contracts/${row.id}/pdf?variant=${variant}`;

  // Stable headers object so pdfjs' ``file`` useMemo (deps include
  // ``fileHeaders``) doesn't recompute on every ContractReviewAndSign
  // re-render. Rebuild only when the bearer token itself changes.
  const pdfFileHeaders = useMemo(
    () => ({ Authorization: `Bearer ${getAccessToken() || ""}` }),
    [],
  );
  // Stable acceptance dispatcher — same reason.
  const handleAccept = useCallback(async (payload) => {
    await api.post(`/portal/contracts/${row.id}/accept`, payload);
    const { data } = await api.get("/portal/contracts");
    const updated = (data.items || []).find((c) => c.id === row.id) || { ...row, status: "signed" };
    setRow(updated);
    onSigned?.(updated);
  }, [row, onSigned]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-stretch p-2 sm:p-4" data-testid="portal-contract-detail">
      <div className="bg-white rounded-lg shadow-xl m-auto w-full max-w-7xl flex flex-col" style={{ maxHeight: "96vh", height: "96vh" }}>
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill status={row.status} />
            <span className="text-sm text-stone-700">
              {row.contract_reference || row.id.slice(0, 12)}
            </span>
          </div>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-800 text-sm border rounded-md px-3 py-1.5"
                  data-testid="portal-contract-close-btn">Close</button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto md:overflow-hidden">
          <ContractReviewAndSign
            contract={row}
            pdfUrl={pdfUrl}
            pdfFileHeaders={pdfFileHeaders}
            downloadUrl={downloadUrl}
            testidPrefix="portal-contract"
            signedNoticeExtra={
              <>
                <div className="text-xs text-emerald-700 mt-1">
                  Accepted by {row.acceptance_record?.signatories?.map((person) => person.full_name).join(" and ")
                    || row.acceptance_record?.signer_identity?.full_name
                    || row.acceptance_record?.franchisee_full_name
                    || "franchisee"}
                </div>
                <div className="text-xs text-emerald-700 mt-2">
                  The signed PDF above is the immutable record. Download or
                  print it for your files.
                </div>
              </>
            }
            onAccept={handleAccept}
            allowDualAcceptance
            onDualSigned={(updated) => { setRow(updated); onSigned?.(updated); }}
          />
        </div>
      </div>
    </div>
  );
}
